import React, { useEffect, useState, useCallback } from 'react';
import { Button, Container, Input } from '@amzn/awsui-components-react';
import { getCaseByCaseId } from 'src/api/getCaseByCaseId';
import Alert from '@amzn/awsui-components-react/polaris/alert';
import { DUPLICATE_REJECTED } from 'src/components/StryfeAPI/Constants';

interface GlobalSearchProps {
  onClick: (data: any, status: string) => void;
}

interface InvoiceResponse {
  data: any;
  status: string;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ onClick }) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    setIsDuplicate(false);
    setError('');
  }, [searchValue]);

  const handleSearch = useCallback(async () => {
    if (!searchValue.trim()) {
      setError('Please enter a barcode to search. Eg: Pilot_Pilot_70000122101332.pdf');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const invoiceDetails: InvoiceResponse = await getCaseByCaseId(searchValue);
      if(!invoiceDetails.status) {
        setError(`Invoice details not available for the given Barcode: ${searchValue}`);
      }
      if (invoiceDetails.status === DUPLICATE_REJECTED) {
        setIsDuplicate(true);
      }
      onClick(invoiceDetails.data, invoiceDetails.status);
    } catch (err) {
      setError('Failed to fetch invoice details. Please try again.');
      console.error('Error fetching invoice details:', err);
    } finally {
      setIsLoading(false);
    }
  }, [searchValue, onClick]);

  const handleInputChange = (event: { detail: { value: string } }) => {
    setSearchValue(event.detail.value);
  };

  return (
    <div role="search" aria-label="Global barcode search">
      <Container
        header={<h2>Search for specific Barcode</h2>}
      >
        <Input
          value={searchValue}
          placeholder="Enter the Barcode to search"
          onChange={handleInputChange}
          disabled={isLoading}
          aria-label="Barcode search input"
          type="search"
        />

        {isDuplicate && (
          <Alert
            header="Duplicate Record"
            type="warning"
            dismissible={false}
          />
        )}

        {error && (
          <Alert
            header="Error"
            type="error"
            dismissible={false}
          >
            {error}
          </Alert>
        )}

        <Button
          variant="primary"
          onClick={handleSearch}
          loading={isLoading}
          disabled={!searchValue.trim() || isLoading}
          ariaLabel="Search barcode"
        >
          Submit
        </Button>
      </Container>
    </div>
  );
};

export default GlobalSearch;
